// React.
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

// Third-parties.
import { Column, usePagination, useSortBy, useTable } from 'react-table';
import { CSVLink } from 'react-csv';

// Types.
import { Deadlock } from '../../../types/instance/Deadlock';

// Components.
import Alert from "../../Alert";
import ReactTable from "../../ReactTable";
import { TimeRangeContext } from '../../../context/TimeRangeContext';
import { buildRedirectQuery } from '../../../helpers/timeRangeUtils';
import TableCSVExport from '../../../component/TableCSVExport';

// Helper.
import { getDefaultPageSize } from '../../../helpers/utils';
import Helper from "../../../helpers/Helper";
import { FilterType } from "../../../typescript/Enums";
import FilterOption from "../../../types/instance/FilterOption";
import ChatButton from "../../chatGPT/ChatButton";
import Prompts from "../../chatGPT/Prompts";
import SearchInput from '../../SearchInput';

export default function DeadlocksTable(props: {
    loading: boolean,
    instanceType: string,
    error: string | null, deadlocks: Deadlock[], filterOptions: FilterOption[], setFilterOptions: Function
}) {
    const { loading, error, deadlocks } = props;
    const timeRangeContext = useContext(TimeRangeContext);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus({ preventScroll: true });
        }
    }, []);

    const navigate = useNavigate();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const [tableFilter, setTableFilter] = useState(query.get("filter") || "");
    const initialPageIndex = Number(query.get("page") || 0);
    const initialPageSize = Number(query.get("pageSize") || getDefaultPageSize());

    // CSV headers
    const headers: any[] = useMemo(() =>
                    props.instanceType !== 'oracle' ? [
                        {
                            label: 'Time',
                            key: 'timeslice'
                        },
                        {
                            label: 'Victim Session',
                            key: 'victimsession'
                        },
                        {
                            label: 'Other Session',
                            key: 'othersession'
                        },
                        {
                            label: 'Raw Data',
                            key: 'rawdata'
                        }
                    ] : [
                        {
                            label: 'Time',
                            key: 'timeslice'
                        },
                        {
                            label: 'Record Id',
                            key: 'recordid'
                        },
                        {
                            label: 'Module Id',
                            key: 'moduleid'
                        },
                        {
                            label: 'Process Id',
                            key: 'processid'
                        },
                        {
                            label: 'Message',
                            key: 'messagetext'
                        }
                    ],
            []);

    const getEmptySessionFilters = () => {
        const newFilter = props.filterOptions
        newFilter.forEach(item => {
            if (item.filterType === FilterType.Sessions) {
                item.filters = [];
            }
        });
        return newFilter
    }


    // Table columns
    const columns: Column[] = useMemo(() => {
                function getActivityUrl(id: number, session: string): string {
                    return '/instances/' + id + '/activity?' +
                            buildRedirectQuery(timeRangeContext.timeRange) + `${session ? `&sessions=${session}` : ''}`
                }

                return props.instanceType !== 'oracle' ? [
                    {
                        Header: 'Time',
                        accessor: 'timeslice',
                        headerClassName: 'text',
                        className: 'concatenate',
                        canSort: true,
                        sortDescFirst: false,
                        sortType: 'basic',
                        Cell: ({ cell }) =>
                                Helper.parseTimesliceWithTz(String(cell.value)).format('YYYY-MM-DD HH:mm')
                    },
                    {
                        Header: 'Victim Session',
                        accessor: 'victimsession',
                        headerClassName: 'text',
                        className: 'concatenate',
                        canSort: true,
                        sortDescFirst: false,
                        sortType: 'basic',
                        Cell: ({ cell, row }: any) => {
                            const sessions = cell.value ? cell.value.split(',') : []
                            return sessions.map((item: string, index: number) => <Link key={index}
                                                                                       onClick={() => props.setFilterOptions(Helper.updateFilterOptions(FilterType.Sessions, item, getEmptySessionFilters()))}
                                                                                       to={getActivityUrl(row.original.id, item)}>{item}{index < sessions.length - 1 ? ', ' : ''}</Link>
                            )
                        }
                    },
                    {
                        Header: 'Other Session',
                        accessor: 'survivorsession',
                        headerClassName: 'text',
                        className: 'concatenate',
                        canSort: true,
                        sortDescFirst: false,
                        sortType: 'basic',

                        Cell: ({ cell, row }: any) => {
                            const sessions = cell.value ? cell.value.split(',') : []
                            if (sessions && sessions.length) {
                                return sessions.map((item: string, index: number) => {

                                            return <Link key={index}
                                                         onClick={() => props.setFilterOptions(Helper.updateFilterOptions(FilterType.Sessions, item, getEmptySessionFilters()))}
                                                         to={getActivityUrl(row.original.id, item)}>{item}{index < sessions.length - 1 ? ', ' : ''}</Link>
                                        }
                                )
                            } else {
                                return <Link
                                        onClick={() => props.setFilterOptions(Helper.updateFilterOptions(FilterType.Sessions, row.original.id, getEmptySessionFilters()))}
                                        to={getActivityUrl(row.original.id, '')}>sessions</Link>
                            }
                        }

                    },

                    {
                        Header: '',
                        accessor: 'sendToDBmarlin',
                        headerClassName: 'text',
                        className: 'text-nowrap',
                        disableSortBy: true,
                        sortDescFirst: false,
                        sortType: 'false',
                        Cell: ({ row }: any) => {
                            return <ChatButton value={Prompts.getDeadLockPrompt(row.values.rawdata, props.instanceType)}/>
                        }

                    },
                    {
                        Header: 'Raw Data',
                        accessor: 'rawdata',
                        headerClassName: 'text',
                        className: 'concatenate',
                        canSort: false,
                        sortDescFirst: false,
                        sortType: 'basic',
                        Cell: ({ row }: any) => {
                            return <CSVLink role="button" data={row.values.rawdata.replace(/"/g, "'")}
                                            enclosingCharacter={`"`}
                                            className="btn btn-xsm btn-primary"
                                            download={'Deadlock' +
                                                    '-' + Helper.parseTimesliceWithTz(String(row.original.timeslice))
                                                            .format('YYYY-MM-DD-HH-mm') +
                                                    '-' + row.original.id +
                                                    '-' + row.original.victimsession +
                                                    '.xml'}
                            >Download</CSVLink>
                        }

                    },
                ] : [{
                    Header: 'Time',
                    accessor: 'timeslice',
                    headerClassName: 'text',
                    className: 'concatenate col-width-10',
                    canSort: true,
                    sortDescFirst: false,
                    sortType: 'basic',
                    Cell: ({ cell }) =>
                            Helper.parseTimesliceWithTz(String(cell.value)).format('YYYY-MM-DD HH:mm')
                }, {
                    Header: 'Message',
                    accessor: 'messagetext',
                    headerClassName: 'text',
                    canSort: true,
                    sortDescFirst: false,
                    sortType: 'basic',
                },

                    {
                        Header: '',
                        accessor: 'sendToDBmarlin',
                        headerClassName: 'text',
                        className: 'text-nowrap',
                        disableSortBy: true,
                        sortDescFirst: false,
                        sortType: 'false',
                        Cell: ({ row }: any) => {
                            return <ChatButton value={Prompts.getDeadLockPrompt(row.values.messagetext, props.instanceType)}/>
                        }

                    },

                    {
                        Header: 'Module ID',
                        accessor: 'moduleid',
                        headerClassName: 'text',
                        className: 'concatenate  col-width-10',
                        canSort: true,
                        sortDescFirst: false,
                        sortType: 'basic',
                    }, {
                        Header: 'Process ID',
                        accessor: 'processid',
                        headerClassName: 'text',
                        className: 'concatenate  col-width-10',
                        canSort: true,
                        sortDescFirst: false,
                        sortType: 'basic',
                    }, {
                        Header: 'Record ID',
                        accessor: 'recordid',
                        headerClassName: 'text',
                        className: 'concatenate  col-width-10',
                        canSort: true,
                        sortDescFirst: false,
                        sortType: 'basic',
                    }]

            },
            [timeRangeContext.timeRange.from.valueOf(), timeRangeContext.timeRange.to.valueOf(), timeRangeContext.timeRange.timezone]);

    const data = useMemo(() => {
                const filteredData = deadlocks.filter((row) =>
                        row.timeslice.toLowerCase().includes(tableFilter.toLowerCase()) ||
                        row.victimsession?.toLowerCase().includes(tableFilter.toLowerCase()) ||
                        row.othersession?.toLowerCase().includes(tableFilter.toLowerCase()) ||
                        row.messagetext?.toLowerCase().includes(tableFilter.toLowerCase()) ||
                        row.moduleid?.toLowerCase().includes(tableFilter.toLowerCase()) ||
                        row.processid?.toString().toLowerCase().includes(tableFilter.toLowerCase()) ||
                        row.recordid?.toString().toLowerCase().includes(tableFilter.toLowerCase()))
                return filteredData
            }, [deadlocks, tableFilter]
    )

    const tableInstance = useTable(
            {
                columns,
                data,
                initialState:
                        {
                            pageIndex: initialPageIndex,
                            pageSize: initialPageSize,
                            sortBy:
                                    [
                                        {
                                            id: 'timeslice',
                                            desc: true
                                        }
                                    ]
                        },
                disableSortRemove: true
            }, useSortBy, usePagination);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        setPageSize,
        pageOptions,
        pageCount,
        canPreviousPage,
        previousPage,
        canNextPage,
        nextPage,
        gotoPage,
        state: { pageIndex, pageSize }
    } = tableInstance;

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        if (tableFilter) {
            params.set('filter', tableFilter);
        } else {
            params.delete('filter');
        }

        params.set('page', pageIndex.toString());
        params.set('pageSize', pageSize.toString());

        navigate({ search: params.toString() }, { replace: true });
    }, [tableFilter, pageIndex, pageSize]);

    useEffect(() => {
        if (tableFilter) {
            gotoPage(0);
        }
    }, [tableFilter]);

    function clearTableFilter() {
        setTableFilter('');
    }

    return (
            <div className="card">
                <div className="card-header">
                    <i className="fal fa-scroll fa-fw" aria-hidden="true"/>
                    Deadlocks
                    <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                       data-bs-target="#collapseStatements" aria-expanded="false" aria-controls="collapseStatements"/>
                </div>
                <div id="collapseStatements" className="card-body collapse show">
                    {Number(process.env.REACT_APP_API_LIMIT) - 1 === data.length - 1 &&
                            (<Alert
                                    message={`To help preserve performance, we limit the total number of statement records below to the top ${Number(process.env.REACT_APP_API_LIMIT) - 1} in descending order by the number of executions.`}
                                    heading="Statement records" variant="alert-info"/>)}
                    <div className="row row-cols-1 row-cols-md-2 table-search">
<div className="col col-md-10">
                    <TableCSVExport
                        tableInstance={tableInstance}
                        filename="Deadlocks"
                        headers={headers}
                    />
                        </div>
                        <div className="col col-md-2">
                            <SearchInput
                                ref={inputRef}
                                value={tableFilter}
                                onChange={(e) => setTableFilter(e.target.value)}
                                onClear={clearTableFilter}
                            />
                        </div>
                    </div>
                    <div className="deadlocks">
                        <ReactTable
                                getTableProps={getTableProps}
                                getTableBodyProps={getTableBodyProps}
                                headerGroups={headerGroups}
                                prepareRow={prepareRow}
                                page={page}
                                canPreviousPage={canPreviousPage}
                                canNextPage={canNextPage}
                                pageOptions={pageOptions}
                                pageCount={pageCount}
                                gotoPage={gotoPage}
                                nextPage={nextPage}
                                previousPage={previousPage}
                                setPageSize={setPageSize}
                                pageIndex={pageIndex}
                                pageSize={pageSize}
                                dataLength={data.length}
                                error={error}
                                loading={loading}
                        />
                    </div>
                </div>
            </div>
    )
}
